/**
 * ChatPanel API Communication
 *
 * Handles all API interactions for the chat panel including
 * streaming and non-streaming message sending.
 *
 * This module extracts API communication from panel.ts to reduce its size
 * and improve maintainability while preserving all functionality.
 */

import type { WidgetConfig, ChatMessage, MessageBlock } from '../../core/config';
import type { ToolRequest, ToolResult } from '../../core/api-executor';
import { ApiClient, ApiError } from '../../core/api-client';
import { StreamingClient, StreamingRenderer } from '../../core/streaming-client';
import { SessionManager, saveApiKey, getApiKey, clearApiKey } from '../../core/session';
import { recordMessageSent } from '../../core/input-validator';
import { debug } from '../../core/logger';
import { generateMessageId } from './state';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Result of sending a message via API
 */
export interface SendMessageResult {
  success: boolean;
  message?: ChatMessage;
  error?: string;
  requiresApiKey?: boolean;
  isInvalidKey?: boolean;
}

/**
 * Callbacks for streaming events
 */
export interface StreamCallbacks {
  onStart?: (sessionId: string) => void;
  onToken?: (token: string) => void;
  onBlock?: (block: MessageBlock) => void;
  onToolRequest?: (request: ToolRequest) => Promise<ToolResult>;
  /** Called when more blocks are being generated (for loading indicators) */
  onLoading?: () => void;
  /** Called when backend sends the resolved language code */
  onLanguage?: (language: string) => void;
  onDone?: (content: string, blocks?: MessageBlock[]) => void;
  onError?: (error: string) => void;
}

/**
 * Options for streaming request
 */
export interface StreamOptions {
  speedMode?: boolean;
  language?: string;
  visitorAuthenticated?: boolean;
}

// =============================================================================
// CHAT PANEL API CLASS
// =============================================================================

/**
 * ChatPanel API handler
 *
 * Encapsulates all API communication logic for cleaner separation of concerns.
 * Handles both streaming and non-streaming message sending, session management,
 * and API key handling.
 */
export class ChatPanelApi {
  private readonly config: WidgetConfig;
  private readonly _apiClient: ApiClient;
  private readonly streamingClient: StreamingClient;
  private readonly sessionManager: SessionManager;

  constructor(config: WidgetConfig) {
    const apiUrl = config.apiUrl ?? 'https://api.promptmysite.com';
    this.config = config;
    this._apiClient = new ApiClient(apiUrl);
    this.streamingClient = new StreamingClient(apiUrl);
    this.sessionManager = new SessionManager(config.workspaceId);

    // Load saved API key
    const savedKey = getApiKey();
    if (savedKey) {
      this._apiClient.setApiKey(savedKey);
      this.streamingClient.setApiKey(savedKey);
    }
  }

  // ===========================================================================
  // STREAMING API
  // ===========================================================================

  /**
   * Send a message using streaming.
   * Creates a StreamingRenderer and manages the streaming lifecycle.
   *
   * @param message The user message to send
   * @param container The container element for streaming output
   * @param onAction Callback for action button clicks
   * @param callbacks Streaming event callbacks
   * @param options Streaming options (speed mode, language)
   */
  async sendStreaming(
    message: string,
    container: HTMLElement,
    onAction: (action: string) => void,
    callbacks: StreamCallbacks,
    options: StreamOptions = {}
  ): Promise<void> {
    const sessionId = this.sessionManager.getSession();
    let streamingRenderer: StreamingRenderer | null = null;
    let hasStarted = false;

    try {
      await this.streamingClient.stream(
        this.config.workspaceId,
        sessionId,
        message,
        {
          onStart: (newSessionId) => {
            this.sessionManager.updateSession(newSessionId);
            streamingRenderer = new StreamingRenderer(container, onAction);
            streamingRenderer.start();
            streamingRenderer.showLoading();
            hasStarted = true;
            callbacks.onStart?.(newSessionId);
          },
          onToken: (token) => {
            streamingRenderer?.addToken(token);
            callbacks.onToken?.(token);
          },
          onBlock: (block) => {
            streamingRenderer?.addBlock(block);
            callbacks.onBlock?.(block);
          },
          onToolRequest: callbacks.onToolRequest,
          onLanguage: (lang) => {
            callbacks.onLanguage?.(lang);
          },
          onLoading: () => {
            // Show loading indicator when more blocks are coming
            streamingRenderer?.showLoading();
            callbacks.onLoading?.();
          },
          onDone: () => {
            streamingRenderer?.complete();
            const content = streamingRenderer?.getContent() || '';
            const blocks = streamingRenderer?.getBlocks();
            recordMessageSent();
            callbacks.onDone?.(content, blocks?.length ? blocks : undefined);
          },
          onError: (error) => {
            // Clean up streaming renderer if it was started
            if (hasStarted && streamingRenderer) {
              streamingRenderer.destroy();
              streamingRenderer = null;
            }

            // Check for API key errors before streaming started
            if (!hasStarted && (error.toLowerCase().includes('api key') || error.includes('401'))) {
              callbacks.onError?.(error);
              return;
            }
            callbacks.onError?.(error || 'Connection error. Please try again.');
          }
        },
        options.speedMode,
        options.language,
        options.visitorAuthenticated
      );
    } catch (error) {
      // Clean up streaming renderer on exception
      if (streamingRenderer !== null) {
        (streamingRenderer as StreamingRenderer).destroy();
        streamingRenderer = null;
      }

      const errorMessage = error instanceof Error ? error.message : 'Unable to connect. Please try again.';
      callbacks.onError?.(errorMessage);
    }
  }

  // ===========================================================================
  // NON-STREAMING API
  // ===========================================================================

  /**
   * Send a message without streaming (fallback mode).
   *
   * @param message The user message to send
   * @returns Result containing the assistant message or error
   */
  async sendNonStreaming(message: string): Promise<SendMessageResult> {
    const sessionId = this.sessionManager.getSession();

    try {
      const response = await this._apiClient.orchestrate(
        this.config.workspaceId,
        sessionId,
        message
      );

      if (response.sessionId) {
        this.sessionManager.updateSession(response.sessionId);
      }

      recordMessageSent();

      return {
        success: true,
        message: {
          id: generateMessageId(),
          role: 'assistant',
          content: response.message,
          blocks: response.blocks,
          timestamp: new Date(),
        },
      };
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.isApiKeyRequired()) {
          return {
            success: false,
            error: error.message,
            requiresApiKey: true,
          };
        }
        if (error.isInvalidApiKey()) {
          return {
            success: false,
            error: error.message,
            requiresApiKey: true,
            isInvalidKey: true,
          };
        }
        if (error.isNetworkError()) {
          return {
            success: false,
            error: 'Unable to connect. Please check your internet connection.',
          };
        }
        if (error.isRateLimited()) {
          return {
            success: false,
            error: 'Too many messages. Please wait a moment.',
          };
        }
        if (error.isServerError()) {
          return {
            success: false,
            error: 'Server error. Please try again later.',
          };
        }
      }

      return {
        success: false,
        error: 'Something went wrong. Please try again.',
      };
    }
  }

  // ===========================================================================
  // SESSION MANAGEMENT
  // ===========================================================================

  /**
   * Get the current session ID
   */
  getSessionId(): string {
    return this.sessionManager.getSession();
  }

  /**
   * Start a new session
   */
  newSession(): void {
    this.sessionManager.newSession();
  }

  // ===========================================================================
  // API KEY MANAGEMENT
  // ===========================================================================

  /**
   * Set and persist the API key
   */
  setApiKey(apiKey: string): void {
    saveApiKey(apiKey);
    this._apiClient.setApiKey(apiKey);
    this.streamingClient.setApiKey(apiKey);
  }

  /**
   * Clear the stored API key
   */
  clearApiKey(): void {
    clearApiKey();
    this._apiClient.setApiKey(null);
    this.streamingClient.setApiKey(null);
  }

  /**
   * Get the current API key
   */
  getApiKey(): string | null {
    return this._apiClient.getApiKey();
  }

  /**
   * Get the internal API client for block renderer context.
   * Needed by setBlockRendererContext for data-driven blocks.
   */
  getInternalApiClient(): ApiClient {
    return this._apiClient;
  }

  // ===========================================================================
  // FEEDBACK
  // ===========================================================================

  /**
   * Submit feedback for a message.
   * Fire-and-forget - errors are logged but not thrown.
   */
  submitFeedback(messageId: string, rating: 'positive' | 'negative'): void {
    const sessionId = this.sessionManager.getSession();
    this._apiClient.submitFeedback(messageId, sessionId, rating)
      .catch(error => {
        debug('Feedback submission failed:', error);
      });
  }

  // ===========================================================================
  // LIFECYCLE
  // ===========================================================================

  /**
   * Abort any pending requests
   */
  abort(): void {
    this._apiClient.abort();
    this.streamingClient.abort();
  }

  /**
   * Dispose of the API handler.
   * Call this when the panel is destroyed.
   */
  dispose(): void {
    this.abort();
  }
}
